import moment from "moment";
import { v5 as uuid_v5 } from "uuid";

export default {
    setSite(state, site) {
        state.site = site
    },

    setScanRemainingSeconds(state, seconds) {
        state.scanRemainingSeconds = seconds
    },

    setCurrentLinkConfigurations(state, configurations) {
        state.currentLinkConfigurations = configurations
    },

    setExperimental(state, experimental) {
        state.experimental = experimental
    },

    setSystemRole(state, hasSystemRole) {
        state.hasSystemRole = hasSystemRole
    },

    setRedirectUrl(state, url) {
        state.redirectUrl = url
    },

    setManufacturersAndProductModels(state, manufacturers) {
        if (manufacturers.length) {
            state.manufacturers = [...state.manufacturers, ...manufacturers]
            parseAndSetProductModels(state, manufacturers)
        }
    },

    setManufacturersAndExtensionModels(state, manufacturers) {
        state.extensionManufacturers = [...state.extensionManufacturers, ...manufacturers]
        parseAndSetExtensionModels(state, manufacturers)
    },

    setSupportedProductsIds(state, productIds) {
        state.supportedProductIds = productIds
    },

    setProductMetaData(state, data) {
        state.supportedProtocols = { ...state.supportedProtocols, ...data.protocols }
        state.deviceThings = data.things
    },

    setDeviceTemplateThings(state, { deviceTemplateId, configId, data }) {
        state.previousDeviceTemplateThings = state.deviceTemplateThings[deviceTemplateId]
        state.deviceTemplateThings = { ...state.deviceTemplateThings, [deviceTemplateId]: data.things }
        state.protocolDefaults = { ...state.protocolDefaults, [configId]: data.protocols }
        if (data.datapoints) {
            state.deviceTemplateDatapoints = { ...state.deviceTemplateDatapoints, [configId]: data.datapoints }
        }
        if (data.emsCapabilities) {
            state.emsCapabilities = { ...state.emsCapabilities, [configId]: data.emsCapabilities }
        }
        if (data.slaveSpecifications != undefined && data.slaveSpecifications.length > 0) {
            state.slaveSpecifications = { ...state.slaveSpecifications, [configId]: data.slaveSpecifications }
        }
        if (data.slaveDeviceOnly) {
            state.slaveOnlyDevices.push(configId)
        }
    },

    setExtensionData(state, { configId, data }) {
        state.supportedProtocols = { ...state.supportedProtocols, ...data.protocols }
        state.extensionThings = { ...state.extensionThings, [configId]: data.things }
        state.extensionProtocols = { ...state.extensionProtocols, [configId]: data.protocols }
        if (data.emsCapabilities) {
            state.emsCapabilities = { ...state.emsCapabilities, [configId]: data.emsCapabilities }
        }
        if (data.externalPlantControls) {
            const externalPlantControlTypes = data.externalPlantControls.map(control => control.type)
            state.supportedExternalPlantControlTypes = { ...state.supportedExternalPlantControlTypes, [configId]: externalPlantControlTypes }
            for (const type of externalPlantControlTypes) {
                if (!state.externalPlantControls.find(epc => epc.type === type)) {
                    // If the type is not already in the list, add it with default values
                    const uuid = uuid_v5(`external-plant-control-${type}-${state.site?.id}`, uuid_v5.URL);
                    state.externalPlantControls.push({
                        id: uuid,
                        type: type,
                        enabled: false,
                        extensionId: null,
                        componentId: null
                    })
                }
            }
        }
    },

    setSiteConfig(state, config) {
        if (config.locked === true) {
            state.lockedConfig = true
        }
        else {
            state.lockedConfig = false

            state.plannedProducts = config.data.products || []
            state.plannedExtensions = config.data.extensions || []
            state.residualThings = config.data.residualThings

            // Remove `isCmsActive` for existing configs if it exists
            if ('isCmsActive' in config.data.ems) {
                delete config.data.ems.isCmsActive;
            }

            state.emsData = config.data.ems
            state.externalPlantControls = config.data.externalPlantControls || []
            state.latestConfigTimestamp = moment(config.updated_at).toISOString()

            const copiedConfigData = JSON.parse(JSON.stringify(config.data))

            state.savedConfig.products = ('products' in copiedConfigData) ? [...copiedConfigData.products] : []
            state.savedConfig.extensions = { ...copiedConfigData.extensions }
            state.savedConfig.cms = {...copiedConfigData.ems }
        }
    },

    setEmsData(state, emsData) {
        state.emsData = emsData
    },

    setEEBusConfig(state, eebusConfig) {
        if (eebusConfig) {
            state.eebusBeaamSmartHomeIPQRCodeURL = eebusConfig.beaam_smart_home_ip_qr_code_url
            state.eebusBeaamSubjectKeyIdentifier = eebusConfig.beaam_subject_key_identifier
        } else {
            state.eebusBeaamSmartHomeIPQRCodeURL = null
            state.eebusBeaamSubjectKeyIdentifier = null
        }
    },

    setGeneratedSiteConfig(state, config) {
        state.generatedConfig = config
        if (config?.created_at) {
            state.latestConfigTimestamp = config.created_at
        }
    },

    setConfigurationUpdatePending(state, isPending) {
        state.configurationUpdatePending = isPending
    },

    confirmSiteConfigChanges(state) {
        const removedElements = e => !e._removed
        const clearElement = e => {
            delete e._removed
            delete e._added
            delete e._changed
            delete e._dirty
            return e
        }

        state.plannedProducts = state.plannedProducts.filter(removedElements).map(clearElement)
        state.plannedExtensions = state.plannedExtensions.filter(removedElements).map(clearElement)
        state.externalPlantControls = state.externalPlantControls.filter(removedElements).map(clearElement)

        let ems = {...state.emsData}
        delete ems._removed
        delete ems._added
        delete ems._changed
        delete ems._dirty

        state.emsData = ems
    },

    addError(state, details) {
        if (details) {
            state.errors.push(details)
        }
    },

    resetErrors(state) {
        state.errors = []
    },

    cleanUpGridMeters(state) {
        for (const product of state.plannedProducts) {
            if (!product['_removed']) { continue }

            for (const device of product.devices) {
                for (const thing of device.things) {
                    if (state.emsData.chargingPowerValues) {
                        delete state.emsData.chargingPowerValues[thing.thingUuid]
                    }
                }
            }
        }
    },

    addMessage(state, details) {
        if (details) {
            state.messages.push(details)
        }
    },

    resetMessages(state) {
        state.messages = []
    },

    startLoading(state) {
        state.loading = true
    },

    stopLoading(state) {
        state.loading = false
    },

    startSiteSetup(state) {
        state.siteSetup = true
    },

    stopSiteSetup(state) {
        state.siteSetup = false
    },

    startGenerating(state) {
        state.generationSuccess = false
        state.generating = true
    },

    stopGenerating(state) {
        state.generating = false
    },

    startExtensionConfig(state, payload = null) {
        if (payload !== null && payload !== undefined && !('prefill' in payload)) {
            // Legacy editing path: payload is an extension object
            state.editingExtension = payload
            state.extensionDiscoveryPayload = null
        } else {
            // New add path: payload is { prefill } or null
            state.extensionDiscoveryPayload = payload?.prefill ?? null
            state.editingExtension = null
        }
        state.mode = 'extension-config'
    },

    startProductConfig(state, payload = null) {
        if (payload !== null && payload !== undefined && !('prefill' in payload)) {
            // Legacy editing path: payload is a product object with productModelVersionUuid
            const productModelVersion = state.productModelVersions[payload.productModelVersionUuid]
            state.editingProduct = {
                ...payload,
                productModelVersion: productModelVersion,
                productModel: { ...productModelVersion.productModel, manufacturer: productModelVersion.manufacturer },
                manufacturer: productModelVersion.manufacturer
            }
            state.productDiscoveryPayload = null
        } else {
            // New add path: payload is { prefill } or null
            state.productDiscoveryPayload = payload?.prefill ?? null
        }

        state.mode = 'product-config'
    },

    cancelProductConfig(state) {
        state.editingProduct = null
        state.productDiscoveryPayload = null
        state.mode = 'configuration'
    },

    cancelExtensionConfig(state) {
        state.editingExtension = null
        state.extensionDiscoveryPayload = null
        state.mode = 'configuration'
    },

    removeProductConfig(state, product) {
        state.plannedProducts.splice(plannedProductsIndex(state, product), 1, removed_record(product))
        state.plannedThings = markThingsForRemovedDevice(state, product)

        for (let device of product.devices) {
            for (let thing of device.things) {
                if (thing.type == "CLIENT_THING")
                    continue

                state.residualThings.push(thing)
            }
        }

    },

    removeExtensionConfig(state, extension) {
        state.plannedExtensions.splice(plannedExtensionsIndex(state, extension), 1, removed_record(extension))
    },

    undoRemoveProductConfig(state, product) {
        state.plannedProducts.splice(plannedProductsIndex(state, product), 1, undo_record(product))
        state.plannedThings = unmarkThingsForRemovedDevice(state, product)

        const productThings = product.devices.flatMap(device => {
            return device.things.map(thing => thing.thingUuid)
        })

        state.residualThings = state.residualThings.filter(residualThing => {
            return !productThings.includes(residualThing.thingUuid)
        })
    },

    undoRemoveExtensionConfig(state, extension) {
        state.plannedExtensions.splice(plannedExtensionsIndex(state, extension), 1, undo_record(extension))
    },

    saveProductConfig(state, product) {
        const savedProduct = state.generatedConfig?.products?.find(p => p.id === product.id)

        if (!savedProduct) {
            if (state.editingProduct) {
                state.plannedProducts = replacePlannedProduct(state, added_record(product))
            } else {
                state.plannedProducts.push(added_record(product))
            }
        } else {
            state.plannedProducts = replacePlannedProduct(state, changed_record(product))
        }

        state.editingProduct = null
        state.mode = 'configuration'
    },

    saveExtensionConfig(state, {extension, editingExtension}) {
        const savedExtension = state.generatedConfig?.extensions?.[extension.extensionUuid]
        const extensionNotExists = !savedExtension

        if (extensionNotExists) {
            if (editingExtension) {
                const currentExtensionIndex = plannedExtensionsIndex(state, editingExtension)
                state.plannedExtensions.splice(currentExtensionIndex, 1, added_record(extension))
            } else {
                state.plannedExtensions.push(added_record(extension))
            }
        } else {
            const currentExtensionIndex = plannedExtensionsIndex(state, editingExtension)
            state.plannedExtensions.splice(currentExtensionIndex, 1, changed_record(extension))
        }

        state.mode = 'configuration'
    },

    // Removes a thing or the link to another device
    removePlannedThing(state, {thingUuid}) {
        clearEmsReferencesToThing(state, thingUuid)

        const thingIndex = state.plannedThings.findIndex(thing => thing.thingUuid == thingUuid)
        if (thingIndex < 0) {
            return
        }
        state.plannedThings.splice(thingIndex, 1, removed_record(state.plannedThings[thingIndex]))
    },

    removeResidualThing(state, thingUuid) {
        clearEmsReferencesToThing(state, thingUuid)

        const thingIndex = state.residualThings.findIndex(thing => thing.thingUuid == thingUuid)
        if (thingIndex < 0) {
            return
        }

        state.residualThings.splice(thingIndex, 1)
    },

    addResidualThing(state, thing) {
        if (state.residualThings == undefined) {
            state.residualThings = []
        }

        state.residualThings.push(thing)
    },

    saveEmsData(state, data) {
        state.emsData = changed_record(data)
    },

    saveExternalPlantControls(state, data) {
        const index = state.externalPlantControls.findIndex(epc => epc.type === data.type)

        if (index !== -1) {
            // Replace the existing control at the found index
            state.externalPlantControls.splice(index, 1, changed_record(data))
        } else {
            // Add new control
            state.externalPlantControls.push(added_record(data))
        }
    },

    setGenerationSuccess(state) {
        state.generationSuccess = true
    },

    setGenerationError(state) {
        state.generationSuccess = false
    }
}

// Various state helper functions

function replacePlannedProduct(state, updatedProduct) {
    return state.plannedProducts.map(product => {
        return product.id === updatedProduct.id ? updatedProduct : product
    })
}

function changed_record(record) {
    return {
        ...record,
        _dirty: true,
        _changed: true
    }
}

function added_record(record) {
    return {
        ...record,
        _dirty: true,
        _added: true
    }
}

function removed_record(record) {
    return {
        ...record,
        _dirty: true,
        _removed: true,
    }
}

function undo_record(record) {
    delete record._removed

    if(!record._changed && !record._added) {
        delete record._dirty
    }

    return record
}

function plannedProductsIndex(state, product) {
    const compare = (p) => p.id === product.id
    return state.plannedProducts.findIndex(compare)
}

function plannedExtensionsIndex(state, extension) {
    const compare = (e) => e.extensionUuid === extension.extensionUuid
    return state.plannedExtensions.findIndex(compare)
}

function parseAndSetProductModels(state, manufacturersWithProducts) {
    let productModels = {}
    let productModelVersions = {}

    for (let manufacturer of manufacturersWithProducts) {
        for (let productModel of manufacturer.product_models) {
            productModels[productModel.id] = {
                ...productModel,
                manufacturer
            }
            for (let productModelVersion of productModel.product_model_versions) {
                productModelVersions[productModelVersion.id] = {
                    ...productModelVersion,
                    productModel,
                    manufacturer
                }
            }
        }
    }

    state.productModels = productModels
    state.productModelVersions = productModelVersions
}

function parseAndSetExtensionModels(state, manufacturersWithExtensions) {
    let extensionModels = {}
    const featuresSet = new Set()

    for (let manufacturer of manufacturersWithExtensions) {
        for (let extensionModel of manufacturer.extension_models) {
            extensionModels[extensionModel.id] = {
                ...extensionModel,
                manufacturer
            }

            // Extract features from device specifications
            extensionModel.device_specifications?.forEach(spec => {
                if (Array.isArray(spec.features)) {
                    spec.features.forEach(feature => featuresSet.add(feature))
                }
            })
        }
    }

    state.extensionModels = extensionModels
    state.supportedExtensionFeatures = Array.from(featuresSet).sort()
}

function markThingsForRemovedDevice(state) {
    let things = []

    for (let thing of state.plannedThings) {
        things.push(removed_record(thing))
    }

    return things
}

function unmarkThingsForRemovedDevice(state) {
    let things = []

    for (let thing of state.plannedThings) {
        things.push(undo_record(thing))
    }

    return things
}

// When a thing is removed we should ensure any EMS reference to it is also cleared
function clearEmsReferencesToThing(state, thingUuid) {
    if (state.emsData.chargingPowerValues) {
        delete state.emsData.chargingPowerValues[thingUuid]
    }
}
